import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Crosshair, UserCheck, Repeat } from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { SEO as META, VALUES, CERTIFICATIONS } from "../content";

const VALUE_ICONS = { Crosshair, UserCheck, ShieldCheck, Repeat };

export default function About() {
  return (
    <>
      <SEO {...META.about} />

      {/* HERO */}
      <section
        className="relative isolate flex min-h-[55vh] items-center overflow-hidden md:min-h-[60vh]"
        data-testid="about-hero"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/hero-about.jpg"
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-scrim-light" />
        </div>
        <div className="container-pad py-20 md:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/90 backdrop-blur-sm">
                About Ridgehawk
              </span>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                The Ridgehawk Story
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                Founded in Milton. Built on precision. Driven by purpose.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="section-pad bg-white" data-testid="about-founder-section">
        <div className="container-pad">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-20">
            <SectionReveal className="lg:col-span-5">
              <div className="sticky top-28">
                <span className="overline">Founder · Operator</span>
                <h2 className="h2 mt-3">A higher standard for local logistics.</h2>
                <div className="mt-8 overflow-hidden rounded-md border border-ridge-border">
                  <img
                    src="/images/founder.jpg"
                    alt="Tajinderpal, founder of Ridgehawk Logistics, in front of a cargo van"
                    className="aspect-[4/5] w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="accent-rule" />
                  <span className="text-sm font-semibold uppercase tracking-[0.18em] text-ridge-charcoal">
                    Tajinderpal · Founder
                  </span>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal className="lg:col-span-7" delay={0.1}>
              <div className="prose-ridge max-w-[68ch] space-y-6 text-base leading-[1.85] text-ridge-text md:text-lg">
                <p>
                  Ridgehawk Logistics was founded by Tajinderpal in Milton, Ontario,
                  with a clear mission: to bring a higher standard to local
                  logistics. After years of experience in enterprise technology
                  leadership and managed services, Taj saw an opportunity to apply
                  the same discipline, precision, and accountability to the courier
                  and logistics industry.
                </p>
                <p>
                  Too many logistics providers treat shipments as transactions. At
                  Ridgehawk, every parcel, pallet, and specimen is a commitment. We
                  built this company on the belief that logistics should be personal
                  — that you should know who's handling your cargo, that safety
                  certifications shouldn't be an afterthought, and that "premium"
                  means consistently exceeding expectations, not just charging more.
                </p>
                <p>
                  The name <span className="font-semibold text-ridge-charcoal">Ridgehawk</span>{" "}
                  reflects who we are: grounded in the landscape of Milton and the
                  Niagara Escarpment, with the sharp vision and precision of a hawk.
                  We see the whole picture — from the first pickup to the final
                  delivery — and we take pride in every mile between.
                </p>
                <p>
                  Whether you're a medical lab that needs compliant specimen
                  transport, a business that needs a dedicated daily route, or a
                  company looking for a reliable last-mile partner, Ridgehawk is
                  built for you.
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/quote"
                  className="btn-accent"
                  data-testid="about-cta-quote"
                >
                  Request a Quote
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="btn-secondary"
                  data-testid="about-cta-services"
                >
                  Explore Services
                </Link>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section
        className="section-pad bg-ridge-stone"
        data-testid="about-values-section"
      >
        <div className="container-pad">
          <SectionReveal>
            <div className="max-w-2xl">
              <span className="overline">What drives us</span>
              <h2 className="h2 mt-3">Four values. No exceptions.</h2>
            </div>
          </SectionReveal>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-ridge-border bg-ridge-border sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, idx) => {
              const C = VALUE_ICONS[v.iconName] || ShieldCheck;
              return (
                <SectionReveal key={v.title} delay={idx * 0.06}>
                  <div
                    className="flex h-full flex-col gap-5 bg-white p-7 md:p-8"
                    data-testid={`value-card-${idx}`}
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-md border border-ridge-charcoal/15 bg-ridge-stone">
                      <C className="h-5 w-5 text-ridge-amber" aria-hidden="true" />
                    </div>
                    <span className="accent-rule" />
                    <h3 className="font-display text-lg font-semibold text-ridge-charcoal">
                      {v.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-ridge-text">
                      {v.body}
                    </p>
                  </div>
                </SectionReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="section-pad bg-white" data-testid="about-certs-section">
        <div className="container-pad">
          <SectionReveal>
            <div className="max-w-2xl">
              <span className="overline">Certified &amp; compliant</span>
              <h2 className="h2 mt-3">
                Compliance is the foundation, not the ceiling.
              </h2>
              <p className="body-muted mt-5 max-w-xl">
                Every Ridgehawk driver carries the certifications below. We exceed the
                standard because your cargo deserves it.
              </p>
            </div>
          </SectionReveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            {CERTIFICATIONS.map((c, idx) => (
              <SectionReveal key={c.title} delay={idx * 0.07}>
                <div
                  className="group relative h-full overflow-hidden rounded-md border border-ridge-border bg-ridge-stone p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-ridge-charcoal/25 md:p-8"
                  data-testid={`cert-card-${idx}`}
                >
                  <div className="absolute right-0 top-0 h-1 w-12 bg-ridge-amber transition-all duration-300 group-hover:w-20" />
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-ridge-charcoal text-white transition-colors group-hover:bg-ridge-amber">
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h3 className="font-display mt-6 text-xl font-semibold text-ridge-charcoal">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-ridge-text">
                    {c.body}
                  </p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
